/**
 * Hooks for Coach Programs and Mindfulness Programs
 * Replaces static API methods with react-query hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from './useBackend';
import { CoachProgram, createCoachProgram } from '../Model/Coach/Program/CoachProgram';
import { MindfulnessProgram, createMindfulnessProgram } from '../Model/Coach/Mindfulness/MindfulnessProgram';
import { FitnessProgramTemplate, createFitnessProgramTemplate } from '../Model/Coach/Program/Template/FitnessProgramTemplate';
import { WorkoutTemplate, createWorkoutTemplate } from '../Model/Coach/SuperMacro/WorkoutTemplate/WorkoutTemplate';

// Coach Program Hooks
export function useCoachProgram(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['coach-program', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/coach/${id}`);
      return json ? createCoachProgram(json) : undefined;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCurrentlyActiveCoachPrograms() {
  const backend = useBackend();

  return useQuery({
    queryKey: ['coach-programs', 'active'],
    queryFn: async () => {
      const json = await backend.get('/coach', { active: true });
      return (json ?? []).map((p: any) => createCoachProgram(p));
    },
    staleTime: 2 * 60 * 1000,
  });
}

// Mindfulness Program Hooks
export function useMindfulnessProgram(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['mindfulness-program', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/coach-program/mindfulness/${id}`);
      return json ? createMindfulnessProgram(json) : undefined;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCurrentlyActiveMindfulnessPrograms() {
  const backend = useBackend();

  return useQuery({
    queryKey: ['mindfulness-programs', 'active'],
    queryFn: async () => {
      const json = await backend.get('/coach-program/mindfulness', { active: true });
      return (json ?? []).map((p: any) => createMindfulnessProgram(p));
    },
    staleTime: 2 * 60 * 1000,
  });
}

// Fitness Program Template Hooks
export function useFitnessProgramTemplate(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['fitness-program-template', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/coach/program/template/${id}`);
      return json ? createFitnessProgramTemplate(json) : undefined;
    },
    enabled: !!id,
    staleTime: 30 * 60 * 1000, // Templates rarely change
  });
}

export function useFitnessProgramTemplates(ids: string[]) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['fitness-program-templates', ids],
    queryFn: async () => {
      if (ids.length === 0) return [];
      const results = await Promise.all(
        ids.map((id) =>
          backend.get(`/coach/program/template/${id}`)
            .then((json) => json ? createFitnessProgramTemplate(json) : undefined)
            .catch(() => undefined)
        )
      );
      return results.filter((t): t is FitnessProgramTemplate => t !== undefined);
    },
    enabled: ids.length > 0,
    staleTime: 30 * 60 * 1000,
  });
}

// Workout Template Hook
export function useWorkoutTemplate(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['workout-template', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/workout/template/${id}`);
      return json ? createWorkoutTemplate(json) : undefined;
    },
    enabled: !!id,
    staleTime: 30 * 60 * 1000, // Templates rarely change
  });
}
